import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setFallingThrough } from '../../Lib/redux/slices/clientApiJourney';
import { setShouldNavigateForward } from '../../Lib/redux/slices/navigationControls';
import { reportClientApiFallThroughOutcomeAction } from '../actionCreators';
import { FALL_THROUGH_DELAY_MS } from '../../Components/ClientApi/clientApiJourney.constants';
import { incrementMetric } from '../../Services/Datadog';

/**
 * Ends the journey as a normal lead: flags the journey as falling through at
 * once (so the timer and progress bar stop), then moves the form on after the
 * shared delay that keeps the last notice on screen, counting the hand-over in
 * Datadog (by reason and form) and reporting the journey outcome to Outreach as
 * it happens. Clears the flag and cancels the hand-over, uncounted and
 * unreported, if the dead end clears or the caller unmounts.
 *
 * @param {Object} options - Options for the hook
 * @param {boolean} options.shouldFallThrough - Whether the caller has hit a dead end
 * @param {string} options.reason - Why the journey is ending; picks the metric tag and the reported outcome
 */
const useFallThroughToNormalLead = ({ shouldFallThrough, reason }) => {
  const dispatch = useDispatch();
  const formId = useSelector((store) => store.formConfig?.formId);

  useEffect(() => {
    if (!shouldFallThrough) return undefined;

    dispatch(setFallingThrough({ isFallingThrough: true }));
    const timer = setTimeout(() => {
      incrementMetric({
        name: 'clientApiJourneyFellThrough',
        tags: { reason, form_id: formId },
      });
      dispatch(reportClientApiFallThroughOutcomeAction({ reason }));
      dispatch(setShouldNavigateForward({ shouldNavigateForward: true }));
    }, FALL_THROUGH_DELAY_MS);

    return () => {
      clearTimeout(timer);
      dispatch(setFallingThrough({ isFallingThrough: false }));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFallThrough]);
};

export default useFallThroughToNormalLead;
